import {
    IonIcon,
    IonLabel,
    IonRouterOutlet,
    IonTabBar,
    IonTabButton,
    IonTabs,
} from '@ionic/react';
import { Trans } from '@lingui/macro';
import {
    checkmarkDoneOutline,
    documentTextOutline,
    hammerOutline,
    homeOutline,
    wifiOutline,
} from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, { memo } from 'react';
import { Redirect } from 'react-router-dom';

import GuardedRoute from '../components/router/GuardedRoute';
import { IInstallationRouteProps } from '../interfaces/IRoute';
import { tabsEnabledAtom } from '../store/Atoms';
import TabAfterClose from './installation/TabAfterClose';
import TabDoc from './installation/TabDoc';
import TabInspect from './installation/TabInspect';
import TabMesh from './installation/TabMesh';
import TabSignature from './installation/TabSignature';
import TabTest from './installation/TabTest';
import TabWire from './installation/TabWire';

const Installation: React.FC<IInstallationRouteProps> = memo(({ match }) => {
    const jobId = match.params.jobId;
    const [tabsEnabled, setTabsEnabled] = useAtom(tabsEnabledAtom);
    const { inspect, wire, mesh, test, doc } = tabsEnabled;
    return (
        <IonTabs>
            <IonRouterOutlet animated={false}>
                <GuardedRoute
                    path="/:jobId/installation/inspect"
                    component={TabInspect}
                />
                <GuardedRoute
                    path="/:jobId/installation/wire"
                    component={TabWire}
                />
                <GuardedRoute
                    path="/:jobId/installation/mesh"
                    component={TabMesh}
                />
                <GuardedRoute
                    exact
                    path="/:jobId/installation/test"
                    component={TabTest}
                />
                <GuardedRoute
                    exact
                    path="/:jobId/installation/doc"
                    component={TabDoc}
                />
                <GuardedRoute
                    exact
                    path="/:jobId/installation/signature"
                    component={TabSignature}
                />
                <GuardedRoute
                    exact
                    path="/:jobId/installation/after-close"
                    component={TabAfterClose}
                />
                <Redirect
                    exact
                    path="/:jobId/installation"
                    to="/:jobId/installation/inspect"
                />
            </IonRouterOutlet>
            <IonTabBar slot="bottom">
                <IonTabButton
                    tab="tab-inspect"
                    href={`/${jobId}/installation/inspect`}
                    disabled={!inspect}
                    onClick={() => {
                        setTabsEnabled({
                            inspect: true,
                            wire: false,
                            mesh: false,
                            test: false,
                            doc: false,
                        });
                    }}
                >
                    <IonIcon icon={homeOutline} />
                    <IonLabel>
                        <Trans>Inspect</Trans>
                    </IonLabel>
                </IonTabButton>
                <IonTabButton
                    tab="tab-wire"
                    href={`/${jobId}/installation/wire`}
                    disabled={!wire}
                    onClick={() => {
                        setTabsEnabled({
                            inspect: true,
                            wire: true,
                            mesh: false,
                            test: false,
                            doc: false,
                        });
                    }}
                >
                    <IonIcon icon={hammerOutline} />
                    <IonLabel>
                        <Trans>Wire</Trans>
                    </IonLabel>
                </IonTabButton>
                <IonTabButton
                    tab="tab-mesh"
                    href={`/${jobId}/installation/mesh`}
                    disabled={!mesh}
                    onClick={() => {
                        setTabsEnabled({
                            inspect: true,
                            wire: true,
                            mesh: true,
                            test: false,
                            doc: false,
                        });
                    }}
                >
                    <IonIcon icon={wifiOutline} />
                    <IonLabel>
                        <Trans>Mesh</Trans>
                    </IonLabel>
                </IonTabButton>
                <IonTabButton
                    tab="tab-test"
                    href={`/${jobId}/installation/test`}
                    disabled={!test}
                    onClick={() => {
                        setTabsEnabled({
                            inspect: true,
                            wire: true,
                            mesh: true,
                            test: true,
                            doc: false,
                        });
                    }}
                >
                    <IonIcon icon={checkmarkDoneOutline} />
                    <IonLabel>
                        <Trans>Test</Trans>
                    </IonLabel>
                </IonTabButton>
                <IonTabButton
                    tab="tab-doc"
                    href={`/${jobId}/installation/doc`}
                    disabled={!doc}
                >
                    <IonIcon icon={documentTextOutline} />
                    <IonLabel>
                        <Trans>Doc</Trans>
                    </IonLabel>
                </IonTabButton>
            </IonTabBar>
        </IonTabs>
    );
});

export default Installation;
